import React, { useCallback, useState } from 'react';
import { Layout, Typography, Tooltip, Avatar, Popover } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import './Header.css';
import { useTranslation } from 'react-i18next';
import logoDefault from '@/assets/images/logo.png';
import UserMenu from '@/shared/components/ui/organisms/UserMenu/UserMenu';
import AppsGridIcon from '@/shared/components/ui/atoms/AppsGridIcon/AppsGridIcon';

export interface HeaderProps {
  /** Optional portal title */
  title?: string;
  /** Custom logo React node (overrides logoSrc) */
  logoNode?: React.ReactNode;
  /** Logo source path (defaults to project logo) */
  logoSrc?: string;
  /** Alt text for logo */
  logoAlt?: string;
  /** Micro contact email (will be uppercased) */
  contactEmail?: string;
  /** Entity name displayed in blue outlined badge */
  entityName?: string;
  /** Show apps launcher icon button */
  showAppsIcon?: boolean;
  /** Handler for apps launcher click */
  onAppsClick?: () => void;
  /**
   * Popover content of the apps launcher (e.g. `<PortalsGrid />`).
   *
   * Receives a `close` callback so the content can dismiss the popover after
   * triggering a navigation. When omitted the apps button is a plain button
   * driven by `onAppsClick`.
   */
  appsMenuContent?: (close: () => void) => React.ReactNode;
  /** User display name (for avatar alt) */
  userName?: string;
  /** User email */
  userEmail?: string;
  /** Avatar image URL */
  avatarUrl?: string;
  /** Optional logout handler (shows logout option in avatar dropdown when provided) */
  onLogout?: () => void;
  /** Whether user can access user management */
  canManageUsers?: boolean;
}

/**
 * Header
 *
 * Unified portal header matching Figma header.svg / right-options.svg.
 * Displays logo, optional title, contact email, entity name button
 * (triggers UserMenu dropdown), apps launcher icon, and user avatar pill.
 *
 * ## Layout (right-options.svg)
 * ```
 * [contact email] [ENTITY NAME ▼] [🔲🔲🔲 👤]
 *                  ↑ triggers       ↑ pill
 *                  dropdown
 * ```
 *
 * ## Apps launcher
 * The 3×3 grid button has two modes:
 * - **plain** (default): a button calling `onAppsClick`
 * - **popover**: when `appsMenuContent` is provided the button opens a popover
 *   with that content (the unified home passes the portals grid)
 *
 * @example Basic usage
 * ```tsx
 * <Header contactEmail="CONTATO.EMPRESA@EMAIL.COM.BR" onLogout={handleLogout} />
 * ```
 *
 * @example Apps launcher with the portals grid
 * ```tsx
 * <Header
 *   showAppsIcon
 *   appsMenuContent={(close) => <PortalsGrid onNavigate={close} />}
 * />
 * ```
 */
export const Header: React.FC<HeaderProps> = ({
  title,
  logoNode,
  logoSrc,
  logoAlt = 'Logo',
  contactEmail,
  entityName,
  showAppsIcon,
  onAppsClick,
  appsMenuContent,
  avatarUrl,
  userName,
  onLogout,
  canManageUsers = false,
}) => {
  const { t } = useTranslation();
  const [appsOpen, setAppsOpen] = useState(false);

  const closeAppsMenu = useCallback(() => setAppsOpen(false), []);

  const appsButton = (
    <button
      type="button"
      className="idf-header__apps-btn"
      onClick={appsMenuContent ? undefined : onAppsClick}
      aria-label={t('Open applications')}
      aria-haspopup={appsMenuContent ? 'true' : undefined}
      aria-expanded={appsMenuContent ? appsOpen : undefined}
      data-testid="apps-btn"
    >
      <AppsGridIcon />
    </button>
  );

  return (
    <Layout.Header className="idf-header" data-node-id="3:2650">
      <div className="idf-header__brand">
        {logoNode ? (
          <div className="idf-header__logo-wrapper">{logoNode}</div>
        ) : (
          <img
            src={logoSrc || logoDefault}
            alt={logoAlt}
            className="idf-header__logo"
          />
        )}
        {title && (
          <Typography.Title level={5} className="idf-header__title">
            {title}
          </Typography.Title>
        )}
      </div>
      <div className="idf-header__actions">
        {(contactEmail || entityName) && (
          <div className="idf-header__infos">
            {contactEmail && (
              <span className="idf-header__infos-email">
                {contactEmail.toLowerCase()}
              </span>
            )}
            {entityName && (
              <span className="idf-header__infos-company">
                {entityName.toUpperCase()}
              </span>
            )}
          </div>
        )}
        <div className="idf-header__user-pill">
          {showAppsIcon &&
            (appsMenuContent ? (
              <Popover
                content={appsMenuContent(closeAppsMenu)}
                trigger="click"
                open={appsOpen}
                onOpenChange={setAppsOpen}
                placement="bottomRight"
                arrow={false}
                overlayClassName="idf-header__apps-popover"
              >
                {appsButton}
              </Popover>
            ) : (
              <Tooltip title={t('Open applications')}>{appsButton}</Tooltip>
            ))}

          {onLogout ? (
            <UserMenu
              avatarUrl={avatarUrl}
              onLogout={onLogout}
              canManageUsers={canManageUsers}
            />
          ) : (
            <Avatar
              src={avatarUrl}
              icon={!avatarUrl && <UserOutlined />}
              size={32}
              className="idf-header__avatar"
              alt={userName || 'user avatar'}
            />
          )}
        </div>
      </div>
    </Layout.Header>
  );
};

export default Header;
